import React, { useRef, useEffect, useCallback } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';

const drawSmoothAreaChart = (ctx, width, height, data, mode, hoverState, isEn) => {
  ctx.clearRect(0, 0, width, height);

  const padLeft = 8;
  const padRight = 44;
  const padTop = 10;
  const padBottom = 20;
  const chartW = width - padLeft - padRight;
  const chartH = height - padTop - padBottom;

  if (chartW <= 0 || chartH <= 0 || !data || data.length === 0) return;

  // Определение максимального значения шкалы
  let rawMax = Math.max(...data);
  let maxVal;
  if (mode === 'pps') {
    rawMax = Math.max(rawMax, 1000);
    if (rawMax <= 5000) maxVal = 5000;
    else if (rawMax <= 10000) maxVal = 10000;
    else if (rawMax <= 25000) maxVal = 25000;
    else if (rawMax <= 50000) maxVal = 50000;
    else if (rawMax <= 100000) maxVal = 100000;
    else if (rawMax <= 200000) maxVal = 200000;
    else maxVal = Math.ceil(rawMax / 50000) * 50000;
  } else {
    rawMax = Math.max(rawMax, 100);
    if (rawMax <= 100) maxVal = 100;
    else if (rawMax <= 200) maxVal = 200;
    else if (rawMax <= 400) maxVal = 400;
    else if (rawMax <= 600) maxVal = 600;
    else if (rawMax <= 1000) maxVal = 1000;
    else maxVal = Math.ceil(rawMax / 200) * 200;
  }

  // 1. Горизонтальная сетка и шкала значений справа (Y-Axis)
  const gridLevels = 3;
  ctx.font = "500 10px 'JetBrains Mono', monospace";
  ctx.textAlign = 'right';

  for (let i = 0; i <= gridLevels; i++) {
    const lineRatio = i / gridLevels;
    const y = padTop + chartH * lineRatio;
    const levelVal = Math.round(maxVal * (1 - lineRatio));

    // Линия сетки
    ctx.beginPath();
    ctx.strokeStyle = i === gridLevels ? 'rgba(255, 255, 255, 0.12)' : 'rgba(255, 255, 255, 0.05)';
    ctx.lineWidth = 1;
    ctx.moveTo(padLeft, y);
    ctx.lineTo(padLeft + chartW, y);
    ctx.stroke();

    // Текст разметки справа
    let labelText = '';
    if (mode === 'pps') {
      if (levelVal >= 1000000) {
        labelText = (levelVal / 1000000).toFixed(1) + 'M';
      } else if (levelVal >= 1000) {
        labelText = Math.round(levelVal / 1000) + 'k';
      } else {
        labelText = levelVal.toString();
      }
      if (i === 0) labelText += ' p/s';
      else if (i === gridLevels) labelText = '0 p/s';
    } else {
      labelText = `${levelVal} ms`;
    }

    ctx.fillStyle = 'rgba(255, 255, 255, 0.45)';
    ctx.fillText(labelText, width - 2, y + 3.5);
  }

  // 2. Шкала времени снизу (X-Axis: -30с, -20с, -10с, Сейчас)
  const timeMarks = [
    { ratio: 0, text: isEn ? '-30s' : '-30с' },
    { ratio: 0.3333, text: isEn ? '-20s' : '-20с' },
    { ratio: 0.6666, text: isEn ? '-10s' : '-10с' },
    { ratio: 1, text: isEn ? 'Now' : 'Сейчас' }
  ];

  timeMarks.forEach((m, idx) => {
    const x = padLeft + chartW * m.ratio;
    const y = padTop + chartH;

    ctx.beginPath();
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.2)';
    ctx.moveTo(x, y);
    ctx.lineTo(x, y + 4);
    ctx.stroke();

    ctx.fillStyle = idx === timeMarks.length - 1 ? '#ffffff' : 'rgba(255, 255, 255, 0.40)';
    if (idx === 0) {
      ctx.textAlign = 'left';
      ctx.fillText(m.text, padLeft, y + 14);
    } else if (idx === timeMarks.length - 1) {
      ctx.textAlign = 'right';
      ctx.fillText(m.text, padLeft + chartW, y + 14);
    } else {
      ctx.textAlign = 'center';
      ctx.fillText(m.text, x, y + 14);
    }
  });

  // 3. Вычисление точек графика
  const stepX = chartW / (data.length - 1);
  const points = data.map((val, idx) => ({
    x: padLeft + idx * stepX,
    y: padTop + chartH - (val / maxVal) * chartH
  }));

  // 4. Градиентная заливка области под кривой
  const fillGradient = ctx.createLinearGradient(0, padTop, 0, padTop + chartH);
  if (mode === 'pps') {
    fillGradient.addColorStop(0, 'rgba(239, 68, 68, 0.25)');
    fillGradient.addColorStop(1, 'rgba(239, 68, 68, 0.0)');
  } else {
    fillGradient.addColorStop(0, 'rgba(255, 175, 40, 0.26)');
    fillGradient.addColorStop(1, 'rgba(255, 75, 40, 0.0)');
  }

  ctx.save();
  const fillPath = new Path2D();
  fillPath.moveTo(points[0].x, points[0].y);
  for (let i = 0; i < points.length - 1; i++) {
    const xc = (points[i].x + points[i + 1].x) / 2;
    const yc = (points[i].y + points[i + 1].y) / 2;
    fillPath.quadraticCurveTo(points[i].x, points[i].y, xc, yc);
  }
  fillPath.lineTo(points[points.length - 1].x, points[points.length - 1].y);
  fillPath.lineTo(padLeft + chartW, padTop + chartH);
  fillPath.lineTo(padLeft, padTop + chartH);
  fillPath.closePath();
  ctx.fillStyle = fillGradient;
  ctx.fill(fillPath);
  ctx.restore();

  // 5. Обводка линии кривой
  ctx.save();
  ctx.beginPath();
  ctx.moveTo(points[0].x, points[0].y);
  for (let i = 0; i < points.length - 1; i++) {
    const xc = (points[i].x + points[i + 1].x) / 2;
    const yc = (points[i].y + points[i + 1].y) / 2;
    ctx.quadraticCurveTo(points[i].x, points[i].y, xc, yc);
  }
  ctx.lineTo(points[points.length - 1].x, points[points.length - 1].y);
  ctx.strokeStyle = mode === 'pps' ? '#ef4444' : '#ffbd2e';
  ctx.lineWidth = 2.0;
  ctx.shadowColor = mode === 'pps' ? 'rgba(239, 68, 68, 0.5)' : 'rgba(255, 189, 46, 0.6)';
  ctx.shadowBlur = 6;
  ctx.stroke();
  ctx.restore();

  // 6. Пульсирующая точка на конце
  const lastPoint = points[points.length - 1];
  ctx.beginPath();
  ctx.arc(lastPoint.x, lastPoint.y, 4, 0, Math.PI * 2);
  ctx.fillStyle = mode === 'pps' ? '#ef4444' : '#ffbd2e';
  ctx.fill();

  // 7. Интерактивный hover (перекрестие, точка и тултип)
  if (hoverState && hoverState.active && hoverState.index >= 0 && hoverState.index < data.length) {
    const targetPt = points[hoverState.index];
    const targetVal = data[hoverState.index];
    const secFromNow = data.length - 1 - hoverState.index;
    const timeStr = secFromNow === 0
      ? (isEn ? 'Now' : 'Сейчас')
      : isEn ? `-${secFromNow}s ago` : `-${secFromNow}с назад`;

    // Вертикальная пунктирная линия
    ctx.save();
    ctx.setLineDash([3, 3]);
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(targetPt.x, padTop);
    ctx.lineTo(targetPt.x, padTop + chartH);
    ctx.stroke();
    ctx.restore();

    // Подсвеченная точка на графике
    ctx.beginPath();
    ctx.arc(targetPt.x, targetPt.y, 6.5, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(255, 255, 255, 0.25)';
    ctx.fill();

    ctx.beginPath();
    ctx.arc(targetPt.x, targetPt.y, 3.5, 0, Math.PI * 2);
    ctx.fillStyle = mode === 'pps' ? '#ffffff' : '#ffbd2e';
    ctx.fill();

    // Всплывающая плашка подсказки
    const tipValStr = mode === 'pps' ? `${targetVal.toLocaleString()} p/s` : `${targetVal} ms`;
    const tipW = 92;
    const tipH = 42;
    let tipX = targetPt.x - tipW / 2;
    let tipY = targetPt.y - tipH - 8;

    if (tipX < padLeft) tipX = padLeft;
    if (tipX + tipW > padLeft + chartW) tipX = padLeft + chartW - tipW;
    if (tipY < padTop) tipY = targetPt.y + 10;

    ctx.save();
    ctx.fillStyle = 'rgba(15, 15, 20, 0.94)';
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.22)';
    ctx.lineWidth = 1;
    ctx.shadowColor = 'rgba(0, 0, 0, 0.6)';
    ctx.shadowBlur = 10;

    ctx.beginPath();
    const r = 8;
    if (ctx.roundRect) {
      ctx.roundRect(tipX, tipY, tipW, tipH, r);
    } else {
      ctx.rect(tipX, tipY, tipW, tipH);
    }
    ctx.fill();
    ctx.stroke();
    ctx.restore();

    // Текст внутри подсказки
    ctx.save();
    ctx.textAlign = 'center';
    ctx.font = "500 9.5px 'JetBrains Mono', monospace";
    ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
    ctx.fillText(timeStr, tipX + tipW / 2, tipY + 15);

    ctx.font = "700 11.5px 'JetBrains Mono', monospace";
    ctx.fillStyle = '#ffffff';
    ctx.fillText(tipValStr, tipX + tipW / 2, tipY + 31);
    ctx.restore();
  }
};

const DdosCharts = ({ ppsHistory = [], rttHistory = [] }) => {
  const { language } = useLanguage();
  const isEn = language === 'en';

  const ppsCanvasRef = useRef(null);
  const rttCanvasRef = useRef(null);

  const ppsHoverRef = useRef({ active: false, x: 0, y: 0, index: -1 });
  const rttHoverRef = useRef({ active: false, x: 0, y: 0, index: -1 });

  const renderCharts = useCallback(() => {
    const ppsCanvas = ppsCanvasRef.current;
    const rttCanvas = rttCanvasRef.current;
    if (!ppsCanvas || !rttCanvas) return;

    const dpr = window.devicePixelRatio || 1;

    // Packets canvas
    const ppsRect = ppsCanvas.parentElement.getBoundingClientRect();
    if (ppsRect.width > 0 && ppsRect.height > 0) {
      if (ppsCanvas.width !== ppsRect.width * dpr || ppsCanvas.height !== ppsRect.height * dpr) {
        ppsCanvas.width = ppsRect.width * dpr;
        ppsCanvas.height = ppsRect.height * dpr;
      }
      const ppsCtx = ppsCanvas.getContext('2d');
      ppsCtx.setTransform(1, 0, 0, 1, 0, 0);
      ppsCtx.scale(dpr, dpr);
      drawSmoothAreaChart(ppsCtx, ppsRect.width, ppsRect.height, ppsHistory, 'pps', ppsHoverRef.current, isEn);
    }

    // Latency canvas
    const rttRect = rttCanvas.parentElement.getBoundingClientRect();
    if (rttRect.width > 0 && rttRect.height > 0) {
      if (rttCanvas.width !== rttRect.width * dpr || rttCanvas.height !== rttRect.height * dpr) {
        rttCanvas.width = rttRect.width * dpr;
        rttCanvas.height = rttRect.height * dpr;
      }
      const rttCtx = rttCanvas.getContext('2d');
      rttCtx.setTransform(1, 0, 0, 1, 0, 0);
      rttCtx.scale(dpr, dpr);
      drawSmoothAreaChart(rttCtx, rttRect.width, rttRect.height, rttHistory, 'rtt', rttHoverRef.current, isEn);
    }
  }, [ppsHistory, rttHistory, isEn]);

  useEffect(() => {
    renderCharts();
  }, [renderCharts]);

  useEffect(() => {
    const handleResize = () => {
      renderCharts();
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [renderCharts]);

  const handleMouseMove = (e, canvas, hoverRef, data) => {
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    const padLeft = 8;
    const padRight = 44;
    const chartW = rect.width - padLeft - padRight;

    if (mouseX >= padLeft && mouseX <= rect.width - padRight && data.length > 0) {
      const ratio = Math.max(0, Math.min(1, (mouseX - padLeft) / chartW));
      const index = Math.round(ratio * (data.length - 1));
      hoverRef.current = { active: true, x: mouseX, y: mouseY, index };
    } else {
      hoverRef.current = { active: false, x: 0, y: 0, index: -1 };
    }
    renderCharts();
  };

  const handleMouseLeave = (hoverRef) => {
    hoverRef.current = { active: false, x: 0, y: 0, index: -1 };
    renderCharts();
  };

  return (
    <section className="charts-section">
      <div className="section-mono-header">
        <div className="section-mono-title">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
          </svg>
          <span>{isEn ? 'Dynamic Monitoring Charts' : 'Динамические графики мониторинга'}</span>
        </div>
      </div>

      <div className="charts-grid">
        {/* График 1: Интенсивность нагрузки */}
        <div className="chart-panel-card">
          <div className="chart-card-header">
            <div className="chart-title-box">
              <div className="chart-heading-text">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 20V10" />
                  <path d="M12 20V4" />
                  <path d="M6 20v-6" />
                </svg>
                <span>{isEn ? 'Stress Load Intensity (Packets/s)' : 'Интенсивность нагрузки (Пакетов/с)'}</span>
              </div>
              <span className="chart-sub-metric">
                {isEn ? 'Generation rate & outbound traffic stream' : 'Скорость генерации и объем исходящего потока'}
              </span>
            </div>
          </div>

          <div className="chart-canvas-wrapper">
            <canvas
              ref={ppsCanvasRef}
              className="retina-chart-canvas"
              onMouseMove={(e) => handleMouseMove(e, ppsCanvasRef.current, ppsHoverRef, ppsHistory)}
              onMouseLeave={() => handleMouseLeave(ppsHoverRef)}
            />
          </div>
        </div>

        {/* График 2: Деградация отклика сервера */}
        <div className="chart-panel-card">
          <div className="chart-card-header">
            <div className="chart-title-box">
              <div className="chart-heading-text">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="12 6 12 12 14 10" />
                </svg>
                <span>{isEn ? 'Server Latency Degradation (RTT ms)' : 'Деградация отклика сервера (RTT ms)'}</span>
              </div>
              <span className="chart-sub-metric">
                {isEn ? 'Latency spike & Netty socket buffer stability' : 'Рост задержки и стабильность очереди Netty'}
              </span>
            </div>
          </div>

          <div className="chart-canvas-wrapper">
            <canvas
              ref={rttCanvasRef}
              className="retina-chart-canvas"
              onMouseMove={(e) => handleMouseMove(e, rttCanvasRef.current, rttHoverRef, rttHistory)}
              onMouseLeave={() => handleMouseLeave(rttHoverRef)}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default DdosCharts;
